import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ConfigService } from '../services/config.service';
import { UsageAnalyticsService } from '../services/usage-analytics.service';
import { Result } from '@shared/models';
import * as Entities from '@shared/entities';

@Injectable({
  providedIn: 'root',
})
export class DispositionsApi {
  private serviceUri = '';

  constructor(
    private http: HttpClient,
    private config: ConfigService,
    private usageAnalytics: UsageAnalyticsService
  ) {
    this.config.Config.subscribe((config) => {
      if (config?.apiUri) {
        this.serviceUri = `${config.apiUri}/dispositions/`;
      }
    });
  }

  search(request: Entities.SearchDispositionsRequest): Observable<Result<Entities.SearchDispositionsResponse>> {
    return this.http.post<Result<Entities.SearchDispositionsResponse>>(this.serviceUri + 'search', request).pipe(
      tap({
        next: (result) => {
          const resultCount = result.successful && result.data ? result.data.records.length : 0;
          this.trackSearch(request, result.successful ? 'Succeeded' : 'Failed', resultCount);
        },
        error: () => this.trackSearch(request, 'Failed', 0),
      })
    );
  }

  getDetails(dispositionId: number | string): Observable<Result<Entities.DispositionDetails>> {
    return this.http.get<Result<Entities.DispositionDetails>>(`${this.serviceUri}${dispositionId}/details`);
  }

  private trackSearch(
    request: Entities.SearchDispositionsRequest,
    outcome: 'Succeeded' | 'Failed',
    resultCount: number
  ): void {
    this.usageAnalytics.trackAction('DispositionSearch', {
      props: {
        Outcome: outcome,
        HasDispositionId: String(request.dispositionId !== undefined),
        HasSessionId: String(!!request.sessionId),
        HasFinalDispositionFilter: String(request.isFinalDisposition !== undefined),
        HasPartnerCallbackProvider: String(!!request.partnerCallbackProvider),
        SortColumn: request.orderBy ?? '',
        SortDirection: request.orderAscending ? 'asc' : 'desc',
      },
      measurements: {
        FilterCount: this.getFilterCount(request),
        ProgramFilterCount: request.programIds?.length ?? 0,
        RetailerFilterCount: request.retailerIds?.length ?? 0,
        DispositionTypeFilterCount: request.dispositionTypeIds?.length ?? 0,
        RouteFilterCount: request.normalizedRoutes?.length ?? 0,
        Five9OutcomeFilterCount: request.five9DeactivationOutcomes?.length ?? 0,
        PartnerOutcomeFilterCount: request.partnerCallbackOutcomes?.length ?? 0,
        DateWindowDays: this.getDateWindowDays(request.dispositionFrom, request.dispositionTo),
        PageNumber: request.pageNumber ?? 1,
        PageSize: request.pageSize ?? 0,
        ResultCount: resultCount,
      },
    });
  }

  private getFilterCount(request: Entities.SearchDispositionsRequest): number {
    return [
      request.dispositionId !== undefined ? 1 : 0,
      request.sessionId ? 1 : 0,
      request.dispositionFrom ? 1 : 0,
      request.dispositionTo ? 1 : 0,
      request.programIds?.length ? 1 : 0,
      request.retailerIds?.length ? 1 : 0,
      request.dispositionTypeIds?.length ? 1 : 0,
      request.normalizedRoutes?.length ? 1 : 0,
      request.isFinalDisposition !== undefined ? 1 : 0,
      request.five9DeactivationOutcomes?.length ? 1 : 0,
      request.partnerCallbackProvider ? 1 : 0,
      request.partnerCallbackOutcomes?.length ? 1 : 0,
    ].reduce((total, count) => total + count, 0);
  }

  private getDateWindowDays(start?: Date, end?: Date): number {
    if (!start || !end) {
      return 0;
    }

    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    return Math.max(0, Math.ceil((end.getTime() - start.getTime()) / millisecondsPerDay));
  }
}
